import type { FacetByBuilderResult } from "../types/instantsearch-types";
import type { ExtractedAttributeData } from "./dynamic-attribute-extractor";
import {
  ATTRIBUTE_NAME_PREFIX,
  ATTRIBUTE_VALUE_GROUP_PREFIX,
  normalizeFilterOptions,
} from "./filter-normalizer";
import {
  BRAND_ID_FIELD,
  CATEGORY_ID_FIELD,
  DISPLAY_ONLY_FILTER_FIELDS,
  MERCHANT_NAME_FIELD,
  SELLER_ID_FIELD,
  isDisplayOnlyFilterField,
} from "../utils/filter-fields";

/**
 * 🏗️ Facet Builder Service (Dynamic Version)
 *
 * Typesense response'undan extract edilen attribute'larla dinamik facet_by oluşturur.
 * Artık mock data kullanmıyor, tamamen runtime'da oluşturulan data ile çalışıyor.
 *
 * Service Responsibility:
 * - Extract edilmiş attribute'lardan facet_by string oluşturma
 * - Facet field listesi sağlama
 * - Attribute metadata mapping sağlama
 * - Filter validation
 *
 * CRITICAL RULE: facet_by asla boş kalmamalı!
 */

export class FacetBuilderService {
  /**
   * Facet field name'den attribute ID çıkar
   *
   * @param facetFieldName - Facet field name (örn: "attributes.attrId")
   * @returns Field name
   */
  extractAttributeIdFromFacetField(facetFieldName: string): string {
    // ✅ Nested faceting: field name olduğu gibi döndür
    return facetFieldName;
  }

  /**
   * Facet filter'ları Typesense filter_by formatına çevir
   *
   * ✅ ATTRIBUTE VALUE ID FILTERING: Tek field ile filtreleme
   *
   * @param facetFilters - UI'dan gelen facet filtreleri { attrId: [valueId1, valueId2] }
   * @returns Typesense filter_by string
   *
   * @example
   * Input: { "attrA": ["valueId1"], "attrB": ["valueId2", "valueId3"] }
   * Output: "productVariants.attributes.attributeValueId:=valueId1 && (productVariants.attributes.attributeValueId:=valueId2 || productVariants.attributes.attributeValueId:=valueId3)"
   *
   * Reference: https://typesense.org/docs/29.0/api/search.html#filter-parameters
   * Simple field filtering: Tek field ile, performanslı
   */
  buildFilterBy(facetFilters: Record<string, string[]>): string {
    const filterConditions: string[] = [];

    Object.entries(facetFilters).forEach(([_attrId, valueIds]) => {
      if (!valueIds || valueIds.length === 0) return;

      // ✅ ATTRIBUTE VALUE ID FILTERING
      // Her valueId zaten unique, tek field ile filtreleme
      // Format: productVariants.attributes.attributeValueId:=xxx

      if (valueIds.length === 1) {
        // Tek value - Direkt attributeValueId ile filtrele
        const valueId = valueIds[0];
        const escapedValueId =
          valueId.includes(" ") || valueId.includes(",")
            ? `\`${valueId}\``
            : valueId;

        // 🚀 PERFORMANS: Tek field, basit filtreleme
        filterConditions.push(`attributes.attributeValueId:=${escapedValueId}`);
      } else {
        // Multiple values - OR ile birleştir
        const valueConditions = valueIds
          .map((valueId) => {
            const escapedValueId =
              valueId.includes(" ") || valueId.includes(",")
                ? `\`${valueId}\``
                : valueId;
            return `attributes.attributeValueId:=${escapedValueId}`;
          })
          .join(" || ");
        // ✅ Multiple values with OR operator (aynı attribute'un farklı değerleri)
        filterConditions.push(`(${valueConditions})`);
      }
    });

    // ✅ Farklı attribute'lar için AND ile birleştir
    return filterConditions.join(" && ");
  }

  /**
   * FilterOptions'ı Typesense filter_by formatına çevir
   *
   * ID-based: brand.id, categories.categoryId. Name field'ları display-only, filter_by'a yazılmaz.
   * ✅ OPTIMIZED: Operasyon sayısını sınırlandırarak Typesense 100 operasyon limitini aşmamak
   *
   * @param filterOptions - Store veya VectorSearch filterOptions
   * @param maxOperations - Maksimum operasyon sayısı (default: 80, Typesense limit: 100)
   * @returns Typesense filter_by string
   *
   * @example
   * Input: { "brand.id": ["69ca..."], "categories.categoryId": ["448"] }
   * Output: "brand.id:=69ca... && categories.categoryId:=448"
   */
  buildFilterByFromOptions(
    filterOptions: Record<string, string[]>,
    maxOperations: number = 80
  ): string {
    const filterConditions: string[] = [];
    let currentOperations = 0;
    const normalizedFilterOptions = normalizeFilterOptions(filterOptions);
    const safeAttributeGroupSuffixes = new Set(
      Object.entries(normalizedFilterOptions)
        .filter(
          ([key, values]) =>
            key.startsWith(ATTRIBUTE_NAME_PREFIX) && values.length > 0,
        )
        .map(([key]) => key.slice(ATTRIBUTE_NAME_PREFIX.length)),
    );

    // Display-only name key'lerini filter_by'a asla yazma
    DISPLAY_ONLY_FILTER_FIELDS.forEach((field) => {
      delete normalizedFilterOptions[field];
    });
    Object.keys(normalizedFilterOptions)
      .filter(isDisplayOnlyFilterField)
      .forEach((field) => delete normalizedFilterOptions[field]);
    delete normalizedFilterOptions.categoryId;

    // ✅ Öncelikli filtreler (ID-based)
    const priorityFields = [
      BRAND_ID_FIELD,
      CATEGORY_ID_FIELD,
      MERCHANT_NAME_FIELD,
      SELLER_ID_FIELD,
      "inStock",
      "attributes.attributeValueId",
    ];

    // ✅ Öncelikli filtreleri işle
    for (const field of priorityFields) {
      const values = normalizedFilterOptions[field];
      if (!values || values.length === 0) continue;

      // ✅ Her filter için maksimum değer sayısı (operasyon limitini korumak için)
      const maxValuesPerFilter = 20; // Her filter için max 20 değer
      const limitedValues = values.slice(0, maxValuesPerFilter);

      // ✅ Operasyon sayısını hesapla: her value için 1 operasyon (:=), OR'lar için +1
      const operationsNeeded =
        limitedValues.length === 1
          ? 1 // Tek value: 1 operasyon (:=)
          : limitedValues.length * 2 - 1; // Multiple values: her value için := + OR'lar

      // ✅ Limit kontrolü: Eğer bu filter'ı eklemek limiti aşarsa, atla
      if (currentOperations + operationsNeeded > maxOperations) {
        // Limit yaklaşıyorsa, sadece ilk birkaç değeri al
        const remainingOps = maxOperations - currentOperations;
        if (remainingOps < 2) break; // En az 2 operasyon gerekli (field:=value)

        // Kalan operasyonlara göre değer sayısını sınırla
        const maxAllowedValues = Math.floor((remainingOps + 1) / 2);
        if (maxAllowedValues < 1) break;

        const finalValues = limitedValues.slice(0, maxAllowedValues);
        if (finalValues.length === 0) continue;

        // ✅ Field name'i escape et
        const escapedField = field.includes(" ") ? `\`${field}\`` : field;

        if (finalValues.length === 1) {
          const value = finalValues[0];
          const escapedValue =
            value.includes(" ") || value.includes(",") || value.includes(":")
              ? `\`${value}\``
              : value;
          filterConditions.push(`${escapedField}:=${escapedValue}`);
          currentOperations += 1;
        } else {
          const valueConditions = finalValues
            .map((value) => {
              const escapedValue =
                value.includes(" ") ||
                value.includes(",") ||
                value.includes(":")
                  ? `\`${value}\``
                  : value;
              return `${escapedField}:=${escapedValue}`;
            })
            .join(" || ");
          filterConditions.push(`(${valueConditions})`);
          currentOperations += finalValues.length * 2 - 1;
        }
        break; // Limit doldu, diğer filtreleri ekleme
      }

      // ✅ Field name'i escape et
      const escapedField = field.includes(" ") ? `\`${field}\`` : field;

      if (limitedValues.length === 1) {
        // Tek value - Direkt field ile filtrele
        const value = limitedValues[0];
        const escapedValue =
          value.includes(" ") || value.includes(",") || value.includes(":")
            ? `\`${value}\``
            : value;

        filterConditions.push(`${escapedField}:=${escapedValue}`);
        currentOperations += 1;
      } else {
        // Multiple values - OR ile birleştir
        const valueConditions = limitedValues
          .map((value) => {
            const escapedValue =
              value.includes(" ") || value.includes(",") || value.includes(":")
                ? `\`${value}\``
                : value;
            return `${escapedField}:=${escapedValue}`;
          })
          .join(" || ");
        filterConditions.push(`(${valueConditions})`);
        currentOperations += limitedValues.length * 2 - 1;
      }
    }

    // Chat seeds preserve the catalog axis for each attribute. Values within
    // one axis are alternatives (OR); different axes are simultaneous
    // constraints (AND). Flattening all ids into the legacy field loses that
    // distinction and widens e.g. `Beden=M + Cinsiyet=Erkek`.
    for (const [key, values] of Object.entries(normalizedFilterOptions)) {
      if (!key.startsWith(ATTRIBUTE_VALUE_GROUP_PREFIX) || values.length === 0)
        continue;
      const suffix = key.slice(ATTRIBUTE_VALUE_GROUP_PREFIX.length);
      if (!safeAttributeGroupSuffixes.has(suffix)) continue;
      const limitedValues = values.slice(0, 20);
      const operationsNeeded = limitedValues.length * 2 - 1;
      if (currentOperations + operationsNeeded > maxOperations) break;
      const conditions = limitedValues.map((value) => {
        const escapedValue =
          value.includes(" ") || value.includes(",") || value.includes(":")
            ? `\`${value}\``
            : value;
        return `attributes.attributeValueId:=${escapedValue}`;
      });
      filterConditions.push(
        conditions.length === 1 ? conditions[0] : `(${conditions.join(" || ")})`
      );
      currentOperations += operationsNeeded;
    }

    // ✅ Farklı field'lar için AND ile birleştir
    return filterConditions.join(" && ");
  }

  /**
   * Facet filter validation - Geçersiz filtreleri temizle
   *
   * @param facetFilters - Mevcut facet filtreleri
   * @param validAttributeIds - Geçerli attribute ID'leri
   * @returns Temizlenmiş facet filtreleri
   */
  validateAndCleanFilters(
    facetFilters: Record<string, string[]>,
    validAttributeIds: string[]
  ): Record<string, string[]> {
    const validFilters: Record<string, string[]> = {};
    const validAttributeSet = new Set(validAttributeIds);

    Object.entries(facetFilters).forEach(([attributeId, valueIds]) => {
      // Sadece geçerli attribute'ları tut
      if (validAttributeSet.has(attributeId) && valueIds.length > 0) {
        validFilters[attributeId] = valueIds;
      }
    });

    return validFilters;
  }

  /**
   * Facet field listesinin geçerli olup olmadığını kontrol et
   *
   * @param facetFields - Kontrol edilecek facet field listesi
   * @returns Boolean - Geçerli mi?
   */
  isFacetFieldListValid(facetFields: string[]): boolean {
    if (!facetFields || facetFields.length === 0) {
      return false;
    }

    // ✅ Artık prefix kontrolü yok, sadece boş olup olmadığını kontrol et
    const isValid = facetFields.every((field) => {
      return field && field.length > 0;
    });

    return isValid;
  }

  /**
   * Debug log - Facet builder durumunu logla
   *
   * @param extractedData - Extract edilmiş data
   * @param result - Builder result
   */
  debugLog(
    _extractedData: ExtractedAttributeData,
    _result: FacetByBuilderResult
  ): void {
    // Debug logging removed
  }
}

/**
 * Singleton instance
 */
export const facetBuilderService = new FacetBuilderService();

/**
 * Build the non-brand portion of a persisted catalogue filter.
 *
 * Attribute name keys are validation metadata for grouped attribute IDs. Keep
 * them until FacetBuilderService has identified the safe groups; the builder
 * removes them before producing Typesense filter_by syntax.
 */
export function buildPersistedOptionFilter(
  filterOptions: Record<string, string[] | undefined>,
): string {
  const activeFilterOptions: Record<string, string[]> = {};
  Object.entries(filterOptions).forEach(([key, values]) => {
    if (values && values.length > 0) activeFilterOptions[key] = values;
  });
  delete activeFilterOptions[BRAND_ID_FIELD];
  return facetBuilderService.buildFilterByFromOptions(activeFilterOptions);
}
